import { useCallback, useEffect, useState } from 'react'
import Boton from '../components/Boton'
import { automataApi } from '../api/automata'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { fmtFechaHora } from '../utils/fecha'
import { colors, sx, text, space } from '../design/tokens'

// "hace cuánto" en castellano, compacto, desde un ISO.
function haceCuanto(iso) {
  if (!iso) return ''
  const t = new Date(iso.replace(' ', 'T')).getTime()
  if (Number.isNaN(t)) return ''
  const seg = Math.max(0, Math.round((Date.now() - t) / 1000))
  if (seg < 60) return 'hace instantes'
  const min = Math.round(seg / 60)
  if (min < 60) return `hace ${min} min`
  const hs = Math.round(min / 60)
  if (hs < 24) return `hace ${hs} h`
  const dias = Math.round(hs / 24)
  return `hace ${dias} d`
}

function fmtDuracion(ms) {
  if (ms == null) return ''
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(1)} s`
}

// Punto de estado de la última corrida.
function EstadoEjecucion({ t }) {
  if (t.nunca_corrio) {
    return <span style={{ ...sx.badge, background: colors.bgSoft, color: colors.textMeta }}>nunca corrió</span>
  }
  if (t.ultimo_estado === 'ok') {
    return <span style={{ ...sx.badge, background: colors.okBg, color: colors.ok }}>✓ ok</span>
  }
  if (t.ultimo_estado === 'fallo') {
    return <span style={{ ...sx.badge, background: colors.errBg, color: colors.err }}>✗ falló</span>
  }
  if (t.ultimo_estado === 'saltado') {
    return <span style={{ ...sx.badge, background: colors.warnBg, color: colors.warn }}>saltado</span>
  }
  return <span style={sx.placeholder}>—</span>
}

export default function AutomataPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(null) // clave en operación

  const cargar = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true)
    try {
      const { data } = await automataApi.listar()
      setRows(data.data || [])
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(() => cargar({ silent: true }), 30000)

  const togglePausa = async (t) => {
    if (!t.pausado && !window.confirm(`¿Pausar la tarea "${t.comando}"?\n\nNo volverá a correr automáticamente hasta que la reanudes.`)) {
      return
    }
    setBusy(t.comando)
    try {
      if (t.pausado) await automataApi.reanudar(t.comando)
      else await automataApi.pausar(t.comando)
      await cargar({ silent: true })
    } catch (e) {
      alert(e.response?.data?.message || 'No se pudo cambiar el estado de la tarea.')
    } finally {
      setBusy(null)
    }
  }

  const activas = rows.filter(r => !r.pausado).length
  const pausadas = rows.length - activas

  return (
    <div>
      <div style={st.header}>
        <div>
          <h1 style={st.h1}>Autómata</h1>
          <p style={st.sub}>
            Todas las tareas automáticas del sistema. La lista se arma sola desde el
            programador: cada cron nuevo aparece acá sin configurar nada.
          </p>
        </div>
        <Boton variante="ghost" sm onClick={() => cargar()}>↻ Refrescar</Boton>
      </div>

      <div style={st.kpis}>
        <span style={st.kpi}><b>{rows.length}</b> tareas</span>
        <span style={st.kpi}><b style={{ color: colors.ok }}>{activas}</b> activas</span>
        {pausadas > 0 && <span style={st.kpi}><b style={{ color: colors.warn }}>{pausadas}</b> pausadas</span>}
      </div>

      <div style={sx.card}>
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>Comando</th>
              <th style={sx.th}>Descripción</th>
              <th style={sx.th}>Cuándo corre</th>
              <th style={sx.th}>Próxima</th>
              <th style={sx.th}>Última ejecución</th>
              <th style={sx.th}>Estado</th>
              <th style={{ ...sx.th, textAlign: 'right' }}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} style={st.empty}>Cargando…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} style={st.empty}>No hay tareas en el programador.</td></tr>
            )}
            {!loading && rows.map(t => (
              <tr key={t.comando} style={{ borderBottom: `1px solid ${colors.borderSoft}`, opacity: t.pausado ? 0.65 : 1 }}>
                <td style={{ ...sx.td, padding: `10px ${space.xl}px` }}>
                  <code style={st.mono}>{t.comando}</code>
                </td>
                <td style={sx.td}>{t.descripcion}</td>
                <td style={sx.td}>
                  <span>{t.cuando}</span>
                  <span style={st.tz}> · {t.timezone}</span>
                </td>
                <td style={{ ...sx.td, ...st.monoCell }}>{t.proxima_at ? fmtFechaHora(t.proxima_at) : '—'}</td>
                <td style={sx.td}>
                  {t.nunca_corrio ? (
                    <span style={sx.placeholder}>—</span>
                  ) : (
                    <div>
                      <span style={st.monoCell}>{fmtFechaHora(t.ultima_corrida_at)}</span>
                      <span style={st.meta}>
                        {' '}· {haceCuanto(t.ultima_corrida_at)}
                        {t.duracion_ms != null && <> · {fmtDuracion(t.duracion_ms)}</>}
                      </span>
                    </div>
                  )}
                </td>
                <td style={sx.td}>
                  <EstadoEjecucion t={t} />
                  {t.pausado && (
                    <span style={{ ...sx.badge, marginLeft: space.sm, background: colors.warnBg, color: colors.warn }}>⏸ pausado</span>
                  )}
                </td>
                <td style={{ ...sx.td, textAlign: 'right' }}>
                  {t.pausado ? (
                    <Boton variante="success" sm disabled={busy === t.comando} onClick={() => togglePausa(t)}>
                      {busy === t.comando ? '…' : '▶ Reanudar'}
                    </Boton>
                  ) : (
                    <Boton variante="secondary" sm disabled={busy === t.comando} onClick={() => togglePausa(t)}>
                      {busy === t.comando ? '…' : '⏸ Pausar'}
                    </Boton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const st = {
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: space.xl, marginBottom: space.lg },
  h1:    { fontSize: text.title + 6, fontWeight: 700, color: colors.text, margin: 0 },
  sub:   { fontSize: text.sec, color: colors.textSec, margin: '4px 0 0', maxWidth: 620 },
  kpis:  { display: 'flex', gap: space['2xl'], marginBottom: space.lg, fontSize: text.body, color: colors.textSec },
  kpi:   { color: colors.textSec },
  mono:  { ...sx.mono, fontSize: text.body, fontWeight: 500, color: colors.text },
  monoCell: { ...sx.mono, fontSize: text.sec, color: colors.text },
  tz:    { fontSize: text.meta, color: colors.textMeta },
  meta:  { fontSize: text.meta, color: colors.textMeta },
  empty: { padding: `${space['3xl']}px ${space.xl}px`, textAlign: 'center', color: colors.textSec, fontSize: text.body },
}
